import {FormBlock, FormDefinition, GroupBlock, ListBlock, StaticTextBlock} from '../models/form-schema.model';
import {buildFieldLabelMap} from './submission-display';

/**
 * Resolves which static-text blocks explain which fields, and **where the author put them** (#501).
 *
 * ### Why the link lives on the text block, not on the field
 * A single paragraph routinely explains several fields at once ("Die folgenden drei Angaben stehen auf Ihrem
 * Sozialversicherungsausweis"), so the natural cardinality is one text → many fields. Modelling it the other way
 * — a `noteId` on each {@link import('../models/form-schema.model').FieldBlock} — would force the author to
 * repeat the same reference on every field, and would push an authoring-only concern into the value-bearing part
 * of the schema that submissions are validated and stored against. Keeping it on
 * {@link StaticTextBlock.explains} leaves fields bit-identical to before, which is why a form that never uses
 * the feature round-trips unchanged.
 *
 * The read-back views want the inverse direction, though — they iterate *fields* — so this module inverts the
 * relation once, at load time, into a flat `fieldKey → {before, after}` map.
 */

/** Whether a note was authored **before** the field it explains, or **after** it. See {@link buildFieldNoteMap}. */
export type NotePlacement = 'before' | 'after';

/** One authored explanation, stamped with where it sits relative to the one field this entry is filed under. */
export interface AttachedNote {
  /** The authored `static-text` block, verbatim — the view reads its `content` and `format` from here. */
  readonly block: StaticTextBlock;
  /** Where the author put it relative to *this* field. The same block can be `before` one field and `after` another. */
  readonly placement: NotePlacement;
}

/**
 * The notes explaining one field, already split by placement so a template never filters (and so `@for` gets a
 * stable array identity instead of a fresh one per change-detection pass).
 *
 * Both lists are in definition order.
 */
export interface FieldNotes {
  /** Notes the author placed before the field — rendered above its value. */
  readonly before: readonly AttachedNote[];
  /** Notes the author placed after the field — rendered below its value. */
  readonly after: readonly AttachedNote[];
}

/** `fieldKey → the notes explaining it`. Fields with no notes are simply absent. */
export type FieldNoteMap = Record<string, FieldNotes>;

/**
 * Builds `fieldKey → {before, after}`, walking the whole definition (groups and lists nested) in
 * **definition order** and recording, per field, whether each attached text was authored before or after it.
 *
 * ### The placement rule — one sentence, on purpose
 * Every block is assigned a **position**: its index in a single pre-order depth-first walk of the definition —
 * pages in order, then the blocks of each page in order, and for a `group`/`list` the container itself followed
 * immediately by its own child blocks. A note explains a field **`before`** it when the note block's position is
 * strictly less than that field block's position, and **`after`** otherwise.
 *
 * That is deliberately the crudest comparison that can express the requirement, because the PDF renderer
 * (`CheqMe.Server.Services.Forms`) implements the *same* rule against the *same* definition and the two must
 * never disagree. Anything subtler — "same page only", "same group only", "nearest field wins" — would have to
 * be reproduced exactly on both sides, and the first divergence would be invisible until a manager compared a
 * screen with a printout. Note also that the rule survives a different *counting* convention: only the relative
 * order of two positions is ever read, and every pre-order depth-first walk of the same definition agrees on
 * that, whether or not it also counts containers.
 *
 * A note explaining several fields is judged **per field**: one paragraph sitting between two questions is
 * `after` the one above it and `before` the one below it, which is exactly how it reads in the form.
 *
 * A key that names **no** field in the definition is silently ignored rather than treated as an error: the field
 * may have been deleted after the text was written, and a dangling reference must never break the rendering of a
 * submission that is otherwise perfectly readable. Same reason a field key appearing twice in one block's
 * `explains` is de-duplicated — the note is shown once, not twice.
 */
export function buildFieldNoteMap(definition: FormDefinition | null | undefined): FieldNoteMap {
  const map: Record<string, {before: AttachedNote[]; after: AttachedNote[]}> = {};
  if (!definition) return map;

  // The set of keys that actually name a field. `buildFieldLabelMap` records leaf fields only (never a group's
  // or a list's own key), which is exactly the set a note can be attached to.
  const fields = buildFieldLabelMap(definition);

  // One walk, one running counter. Fields and notes are collected together so their positions come from the
  // same sequence — comparing two numbers minted by two different walks is how these things drift.
  let position = 0;
  const fieldPositions: Record<string, number> = {};
  const notes: {block: StaticTextBlock; position: number}[] = [];

  const walk = (blocks: FormBlock[]) => {
    for (const block of blocks) {
      // The container is stamped BEFORE its children, so a note outside a group sorts before every field in it
      // when it precedes the group, and after every one of them when it follows.
      const at = position++;
      if (block.type === 'group') walk((block as GroupBlock).blocks);
      else if (block.type === 'list') walk((block as ListBlock).blocks);
      else if (block.type === 'static-text') notes.push({block: block as StaticTextBlock, position: at});
      else {
        const key = (block as {key?: string}).key;
        // Field keys are globally unique in a definition; `??=` keeps the first occurrence if one ever is not.
        if (key && key in fields && !(key in fieldPositions)) fieldPositions[key] = at;
      }
    }
  };

  definition.pages.forEach(page => walk(page.blocks));

  // `notes` is already in definition order, so each bucket ends up in definition order too.
  for (const note of notes) {
    for (const key of new Set(note.block.explains ?? [])) {
      const fieldPosition = fieldPositions[key];
      if (fieldPosition === undefined) continue;
      const placement: NotePlacement = note.position < fieldPosition ? 'before' : 'after';
      (map[key] ??= {before: [], after: []})[placement].push({block: note.block, placement});
    }
  }

  return map;
}

/**
 * The one empty result every note-less field shares.
 *
 * Identity matters here: {@link notesForField} is called from a template, so a fresh `{before: [], after: []}`
 * per change-detection pass would hand Angular's `@for` a new array every tick and make it re-diff a list that
 * never changes.
 */
const NO_NOTES: FieldNotes = Object.freeze({before: Object.freeze([]), after: Object.freeze([])});

/**
 * The notes explaining one field, split into `before` / `after` — empty when it has none, and safe against a
 * missing map (both read-back views load the definition best-effort, so the map is legitimately absent while it
 * is in flight).
 */
export function notesForField(notes: FieldNoteMap | null | undefined, fieldKey: string): FieldNotes {
  return notes?.[fieldKey] ?? NO_NOTES;
}
